import { ReactElement, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";

function evalBoolean(value: boolean | (() => boolean)) {
  return typeof value === "function" ? value() : value;
}

export default function UnsavedChanges({
  unsavedMessage,
  unsavedNote,
  unsavedTags,
  setActiveTab,
}: {
  unsavedMessage: boolean | (() => boolean);
  unsavedNote: boolean | (() => boolean);
  unsavedTags: boolean | (() => boolean);
  setActiveTab: (newTab: "contactDetails" | "tags") => void;
}): ReactElement {
  function getState() {
    let navigateToTabOnCancel: "contactDetails" | "tags" | null = null;
    let message: ReactNode | null = null;

    if (evalBoolean(unsavedMessage)) {
      message = (
        <FormattedMessage
          defaultMessage="You have an unsent message. Are you sure you want to discard your changes?"
          description="Body for the dialog that appears when you try to navigate away from a thread with an unsaved message"
        />
      );
    } else if (evalBoolean(unsavedNote)) {
      message = (
        <FormattedMessage
          defaultMessage="You have unsaved changes to the contact note. Are you sure you want to discard your changes?"
          description="Body for the dialog that appears when you try to navigate away from a thread with an unsaved contact note"
        />
      );
      navigateToTabOnCancel = "contactDetails";
    } else if (evalBoolean(unsavedTags)) {
      message = (
        <FormattedMessage
          defaultMessage="You have unsaved changes to contact tags. Are you sure you want to discard your changes?"
          description="Body for the dialog that appears when you try to navigate away from a thread with a unsaved contact tags"
        />
      );
      navigateToTabOnCancel = "tags";
    }

    return { message, navigateToTabOnCancel };
  }

  return (
    <RouteLeavingGuard
      shouldBlockNavigation={navParams => {
        const { message } = getState();
        if (!message) {
          return false;
        }

        if (navParams.type === "unload") {
          return true;
        }

        const { nextLocation, currentLocation } = navParams;

        // For navigation within the page, we only want to block navigation
        // if they're changing the thread or context, not if they're just
        // changing search params.

        if (nextLocation.pathname !== currentLocation.pathname) {
          return true;
        }

        const currentSearchParams = new URLSearchParams(currentLocation.search);
        const nextSearchParams = new URLSearchParams(nextLocation.search);

        if (
          currentSearchParams.get("thread") !==
            nextSearchParams.get("thread") ||
          currentSearchParams.get("context") !==
            nextSearchParams.get("context") ||
          currentSearchParams.get("assignment") !==
            nextSearchParams.get("assignment")
        ) {
          return true;
        }

        return false;
      }}
      title={
        <FormattedMessage
          defaultMessage="Discard Changes?"
          description="Title for the dialog that appears when you try to navigate away from a thread with unsaved changes"
        />
      }
      cancelButtonText={
        <FormattedMessage
          defaultMessage="Go Back"
          description="Cancel button text for the dialog that appears when you try to navigate away from a thread with unsaved changes"
        />
      }
      continueButtonText={
        <FormattedMessage
          defaultMessage="Discard & Continue"
          description="Continue button text for the dialog that appears when you try to navigate away from a thread with unsaved changes"
        />
      }
      onCancel={() => {
        const { navigateToTabOnCancel } = getState();
        if (navigateToTabOnCancel) {
          setActiveTab(navigateToTabOnCancel);
        }
      }}
    />
  );
}
